import { Badge, Box, Table } from '@chakra-ui/react';
import { fmtDate, fmtMoney } from '@/utils/format';

export type ScheduleRow = {
  installmentNumber: number;
  dueDate: string;
  totalAmount: string;
  principalAmount: string;
  interestAmount: string;
  closingBalance: string;
  paidPrincipal?: string;
  paidInterest?: string;
  adminFeeAmount?: string;
  moraAccrued?: string;
  displayStatus?: 'pending' | 'partial' | 'paid' | 'overdue';
};

const STATUS = {
  paid: { label: 'Pagada', palette: 'green' },
  partial: { label: 'Parcial', palette: 'yellow' },
  overdue: { label: 'Vencida', palette: 'red' },
  pending: { label: 'Pendiente', palette: 'gray' },
} as const;

export function ScheduleTable({ rows, showPaid = false }: { rows: ScheduleRow[]; showPaid?: boolean }) {
  const showAdminFee = rows.some((r) => Number(r.adminFeeAmount ?? '0') > 0);
  const showMora = showPaid && rows.some((r) => Number(r.moraAccrued ?? '0') > 0);
  return (
    <Box borderWidth="1px" borderColor="border" rounded="md" overflow="hidden">
      <Box maxH="460px" overflowY="auto" overflowX="auto">
        <Table.Root size="sm" stickyHeader>
          <Table.Header>
            <Table.Row bg="bg.subtle">
              <Table.ColumnHeader>#</Table.ColumnHeader>
              <Table.ColumnHeader>Fecha</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Cuota</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Capital</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Interés</Table.ColumnHeader>
              {showAdminFee && <Table.ColumnHeader textAlign="end">Costo adm.</Table.ColumnHeader>}
              {showMora && <Table.ColumnHeader textAlign="end">Mora</Table.ColumnHeader>}
              {showPaid && <Table.ColumnHeader textAlign="end">Pagado</Table.ColumnHeader>}
              <Table.ColumnHeader textAlign="end">Saldo</Table.ColumnHeader>
              {showPaid && <Table.ColumnHeader>Estado</Table.ColumnHeader>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((r) => {
              const paid = (Number(r.paidPrincipal ?? '0') + Number(r.paidInterest ?? '0')).toFixed(2);
              const st = r.displayStatus ? STATUS[r.displayStatus] : null;
              return (
                <Table.Row key={r.installmentNumber}>
                  <Table.Cell fontWeight="600">{r.installmentNumber}</Table.Cell>
                  <Table.Cell>{fmtDate(r.dueDate)}</Table.Cell>
                  <Table.Cell textAlign="end">{fmtMoney(r.totalAmount)}</Table.Cell>
                  <Table.Cell textAlign="end">{fmtMoney(r.principalAmount)}</Table.Cell>
                  <Table.Cell textAlign="end">{fmtMoney(r.interestAmount)}</Table.Cell>
                  {showAdminFee && <Table.Cell textAlign="end">{fmtMoney(r.adminFeeAmount ?? '0.00')}</Table.Cell>}
                  {showMora && <Table.Cell textAlign="end">{fmtMoney(r.moraAccrued ?? '0.00')}</Table.Cell>}
                  {showPaid && <Table.Cell textAlign="end">{fmtMoney(paid)}</Table.Cell>}
                  <Table.Cell textAlign="end">{fmtMoney(r.closingBalance)}</Table.Cell>
                  {showPaid && (
                    <Table.Cell>{st && <Badge colorPalette={st.palette}>{st.label}</Badge>}</Table.Cell>
                  )}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  );
}
